import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { useCallback, useEffect, useId, useState } from "react";
import { DrawerContent } from "./DrawerContent";
import { DrawerContext } from "./context";
import type { DrawerProps } from "./types";

export const Drawer = (props: DrawerProps) => {
  const { open, onOpenChange } = props;
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);
  const titleId = useId();
  const descriptionId = useId();

  const { context: floatingContext } = useFloating({
    open,
    onOpenChange,
  });

  const dismiss = useDismiss(floatingContext, {
    enabled: !props.preventClose,
    escapeKey: !props.preventClose,
    outsidePress: props.preventClose ? false : props.closeOnOutsidePress,
  });

  const { getFloatingProps } = useInteractions([dismiss]);

  const handleDismiss = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleAnimationEnd = useCallback(() => {
    if (!open) {
      setMounted(false);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const rafId = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(rafId);
    } else {
      setVisible(false);
    }
  }, [open]);

  if (!mounted && !open) {
    return null;
  }

  const contextValue = {
    ...props,
    handleDismiss,
    floatingContext,
    getFloatingProps,
    handleAnimationEnd,
    mounted,
    visible,
    titleId,
    descriptionId,
  };

  return (
    <DrawerContext.Provider value={contextValue}>
      <FloatingPortal>
        {mounted && (
          <FloatingOverlay className="bg-black/50 z-[60]" lockScroll>
            <FloatingFocusManager
              context={floatingContext}
              returnFocus={true}
              initialFocus={0}
              modal={true}
            >
              <DrawerContent />
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </FloatingPortal>
    </DrawerContext.Provider>
  );
};
